import {Component, Fragment} from "preact";
import {resources} from "../resources";
import {safeLocalStorage} from "../utils/safeLocalStorage";
import {Button} from "./controls/Button";
import style from "./PWA.module.css";

/**
 * The BeforeInstallPromptEvent is fired at the Window.onbeforeinstallprompt handler
 * before a user is prompted to "install" a website to a home screen on mobile.
 *
 * Only supported on Chrome and Android Webview.
 */
interface BeforeInstallPromptEvent extends Event {
	/**
	 * Returns an array of DOMString items containing the platforms on which the event was dispatched.
	 * This is provided for user agents that want to present a choice of versions to the user such as,
	 * for example, "web" or "play" which would allow the user to chose between a web version or
	 * an Android version.
	 */
	readonly platforms: Array<string>;

	/**
	 * Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
	 */
	readonly userChoice: Promise<{
		outcome: "accepted" | "dismissed";
		platform: string;
	}>;

	/**
	 * Allows a developer to show the install prompt at a time of their own choosing.
	 * This method returns a Promise.
	 */
	prompt(): Promise<void>;
}

interface PWAStates {
	showInstallPrompt: boolean;
	ios: boolean;
	deferredPrompt: BeforeInstallPromptEvent | null;
}

export class PWA extends Component<{}, PWAStates> {
	constructor() {
		super();
		this.state = {
			showInstallPrompt: false,
			ios: false,
			deferredPrompt: null,
		};
	}

	private detectOs(): "Windows" | "Android" | "iOS" | "unknown" {
		let userAgent = navigator.userAgent || navigator.vendor;

		// Windows Phone must come first because its UA also contains "Android"
		if (/windows phone/i.test(userAgent)) {
			return "Windows";
		}

		if (/android/i.test(userAgent)) {
			return "Android";
		}

		// iOS detection from: http://stackoverflow.com/a/9039885/177710
		if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any)["MSStream"]) {
			return "iOS";
		}

		return "unknown";
	}

	componentDidMount() {
		if ("serviceWorker" in navigator) {
			navigator.serviceWorker
				.register("/sw.js")
				.then(() => {
					console.log("Service Worker Registered");
				});

			const os = this.detectOs();
			console.log("current os", os);

			if (os === "Android") {
				window.addEventListener("beforeinstallprompt", ev => {
					ev.preventDefault();
					this.setState({
						showInstallPrompt: true,
						deferredPrompt: ev as BeforeInstallPromptEvent,
					});
				});
			}

			if (os === "iOS") {
				let showInstallPrompt = true;

				// detect if user was already shown the prompt
				const timestamp = safeLocalStorage.read("hide_ios_pwa_until");
				if (timestamp && new Date().getTime() < new Date(timestamp).getTime()) {
					showInstallPrompt = false;
				}

				this.setState({
					ios: true,
					showInstallPrompt,
				});
			}
		}
	}

	async handleClick() {
		const {deferredPrompt} = this.state;
		if (deferredPrompt) {
			await deferredPrompt.prompt();
			const {outcome} = await deferredPrompt.userChoice;
			if (outcome === "accepted") {
				console.log("User accepted the A2HS prompt");
			}
			else {
				console.log("User dismissed the A2HS prompt");
			}
			this.setState({
				showInstallPrompt: false,
				deferredPrompt: null,
			});
		}
	}

	handleClose() {
		safeLocalStorage.write("hide_ios_pwa_until", new Date(Date.now() + 1000 * 60 * 60 * 24 * 14).toISOString());
		this.setState({
			showInstallPrompt: false,
		});
	}

	render() {
		return (
			<Fragment>
				{this.state.showInstallPrompt && (
					<div className={style.pwa}>
						{!this.state.ios && (
							<Fragment>
								<span
									className={style.pwaText}
									dangerouslySetInnerHTML={{
										__html: resources.strings.AndroidPwaPrompt,
									}}
								/>
								<Button className={style.pwaButton} onClick={() => this.handleClick()}>
									{resources.strings.Install}
								</Button>
							</Fragment>
						)}
						{this.state.ios && (
							<span
								className={style.pwaText}
								dangerouslySetInnerHTML={{
									__html: resources.strings.IosPwaPrompt,
								}}
							/>
						)}
						<Button className={style.pwaCloseButton} onClick={() => this.handleClose()}></Button>
					</div>
				)}
			</Fragment>
		);
	}
}
